import type { Lang } from "../types.ts";

export const LANGS: { code: Lang; label: string; htmlLang: string }[] = [
  { code: "de", label: "Deutsch", htmlLang: "de" },
  { code: "en", label: "English", htmlLang: "en" },
  { code: "zh", label: "简体中文", htmlLang: "zh-Hans" },
];

/**
 * UI chrome strings. Question content is translated separately in the catalog.
 * Keys are shared across all three languages so a missing one fails typecheck.
 */
const de = {
  appTitle: "Führerschein Theorie",
  appSubtitle: "Lokale Übungs-App",

  // navigation
  home: "Start",
  exam: "Prüfung",
  practice: "Üben",
  results: "Ergebnis",
  back: "Zurück",
  next: "Weiter",
  previous: "Zurück",
  finish: "Prüfung beenden",
  restart: "Neu starten",
  close: "Schließen",

  // home
  startExam: "Prüfung simulieren",
  startExamHint: "30 Fragen aus einem amtlichen Prüfungsbogen, wie in der echten Prüfung.",
  startPractice: "Alle Fragen üben",
  startPracticeHint: "Blättere frei durch den gesamten Fragenkatalog mit Erklärungen.",
  catalogInfo: "Fragenkatalog gültig ab {from}",
  questionCount: "{n} Fragen",

  // languages
  primaryLanguage: "Hauptsprache",
  secondaryLanguage: "Zweitsprache",
  secondaryOff: "aus",
  languageSettings: "Sprachen",
  translationMissing: "Keine Übersetzung vorhanden — deutscher Text wird angezeigt.",

  // question
  question: "Frage",
  questionOf: "Frage {i} von {n}",
  points: "{n} Fehlerpunkte",
  selectAll: "Mehrfachauswahl möglich",
  enterNumber: "Zahl eingeben",
  yourAnswer: "Deine Antwort",
  checkAnswer: "Antwort prüfen",
  correct: "Richtig",
  incorrect: "Falsch",
  correctAnswer: "Richtige Antwort",
  explanation: "Erklärung",
  showExplanation: "Erklärung anzeigen",
  hideExplanation: "Erklärung ausblenden",
  showTranslation: "Übersetzung anzeigen",
  hideTranslation: "Übersetzung ausblenden",
  unanswered: "Nicht beantwortet",
  answered: "Beantwortet",
  playVideo: "Video abspielen",
  videoQuestion: "Videofrage",
  videoHint: "Sieh dir das Video an, bevor du antwortest.",
  startFilm: "Film starten",
  watchFilmPrompt: "Bitte starte den Film, um dich mit der Situation vertraut zu machen.",
  toAnswers: "Zur Frage",
  replayFilm: "Film erneut starten",
  filmPlaysLeft: "Du kannst den Film noch {n}× ansehen.",
  filmPlaysUsedUp: "Der Film kann nicht noch einmal angesehen werden.",
  questionDetail: "Fragendetails",
  detailNumber: "Fragennummer",
  detailTopic: "Thema",
  detailPoints: "Fehlerpunkte",
  detailPractised: "Geübt",
  detailNeverPractised: "Noch nicht beantwortet.",
  detailLastPractised: "Zuletzt geübt am {date}",
  detailTimesWrong: "× falsch",
  detailTimesRight: "× richtig beantwortet",
  share: "Teilen",
  ok: "OK",

  // exam
  examSet: "Bogen {n}",
  examProgress: "{answered} von {total} beantwortet",
  confirmFinish: "Prüfung beenden und auswerten?",
  confirmFinishOpen: "Es sind noch {n} Fragen offen. Trotzdem auswerten?",
  cancel: "Abbrechen",
  confirm: "Auswerten",

  // results
  passed: "Bestanden",
  failed: "Nicht bestanden",
  errorPoints: "Fehlerpunkte",
  errorPointsOf: "{points} von maximal {max} Fehlerpunkten",
  scoreCorrect: "{correct} von {total} richtig",
  resultSummary: "Auswertung",
  reviewMistakes: "Fehler durchsehen",
  reviewAll: "Alle Fragen durchsehen",
  failReasonPoints: "Mehr als {max} Fehlerpunkte.",
  failReasonTwoFive: "Zwei Fragen mit 5 Fehlerpunkten falsch beantwortet.",
  passRule: "Bestanden mit höchstens {max} Fehlerpunkten und nicht zwei 5-Punkte-Fragen falsch.",
  byCategory: "Nach Themen",
  timeTaken: "Bearbeitungszeit",
  newExam: "Neue Prüfung",

  // practice
  filterAll: "Alle",
  filterMistakes: "Meine Fehler",
  filterUnseen: "Noch nicht gesehen",
  filterVideo: "Videofragen",
  jumpToQuestion: "Zu Frage springen",
  first: "Erste Frage",
  last: "Letzte Frage",
  back10: "10 Fragen zurück",
  forward10: "10 Fragen weiter",
  searchPlaceholder: "Fragen durchsuchen…",
  noResults: "Keine Fragen gefunden.",
  resetProgress: "Fortschritt zurücksetzen",
  resetProgressConfirm: "Allen gespeicherten Fortschritt löschen?",

  // shortcuts
  shortcuts: "Tastenkürzel",
  version: "Version",
  sw_unsupported: "Offline nicht verfügbar",
  sw_off: "Offline wird vorbereitet…",
  sw_installing: "Offline wird eingerichtet…",
  sw_active: "Offline verfügbar",
  sw_waiting: "Update bereit",
  sw_failed: "Offline nicht eingerichtet",
  settings: "Einstellungen",
  settingsHint: "Offline-Nutzung, Speicher und Version.",
  offlineSection: "Offline-Nutzung",
  cachedMedia: "Gespeicherte Mediendateien",
  storageUsed: "Belegter Speicher",
  precacheHint: "Alles im Voraus laden — Fragenkatalog, Bilder und Filme, rund 400 MB. Danach funktioniert die App vollständig ohne Netz.",
  precacheStart: "Alles offline verfügbar machen",
  precacheProgress: "{done} von {total} geladen…",
  precacheDone: "{n} Dateien gespeichert.",
  precacheFailed: "{n} fehlgeschlagen",
  versionCurrent: "Aktuelle Version.",
  versionStale: "veraltet",
  updateAvailable: "Eine neue Version ist verfügbar.",
  reloadNow: "Neu laden",
  shortcutsTitle: "Tastenkürzel",
  scToggleAnswer: "Antwort {n} auswählen",
  scNext: "Nächste Frage",
  scPrev: "Vorherige Frage",
  scCheck: "Antwort prüfen / bestätigen",
  scExplain: "Erklärung ein-/ausblenden",
  scTranslate: "Übersetzung ein-/ausblenden",
  scVideo: "Video abspielen",
  scSwapLang: "Sprachen tauschen",
  scCycleSecondary: "Zweitsprache wechseln",
  scHelp: "Diese Hilfe anzeigen",
  scHome: "Zurück zum Start",
  scFinish: "Prüfung auswerten",

  // a11y
  skipToContent: "Zum Inhalt springen",
  loading: "Wird geladen…",
  loadError: "Fragenkatalog konnte nicht geladen werden.",
  mediaUnavailable: "Medien nicht verfügbar",

  // exam toolbar
  pointsShort: "Punkte",
  remainingTasks: "noch {n} Aufgaben",
  evaluate: "Auswertung",
  markQuestion: "Frage markieren",
  unmarkQuestion: "Markierung entfernen",
  basicKnowledge: "Grundstoff",

  // training menu
  training: "Training",
  newQuestions: "30 neue Fragen",
  newQuestionsHint: "Fragen, die du noch nicht 2× nacheinander richtig beantwortet hast.",
  allQuestions: "Alle Fragen",
  specialTopics: "Spezialthemen",
  specialTopicsHint: "Nach Themen wie Vorfahrt oder Verkehrszeichen üben.",
  searchQuestions: "Fragen suchen",
  lastMistakes: "{n} letzte Fehler",
  markedQuestions: "{n} markierte Fragen",
  allFit: "Alle Fragen sind schon „fit für die Prüfung“ (zuletzt 2× richtig). Übe ggf. unter „Alle Fragen & Themen“ weiter.",
  noMarked: "Du hast noch keine Fragen markiert.",
  installTitle: "Zum Home-Bildschirm hinzufügen",
  installHint: "Safari fragt das nie von selbst: auf das Teilen-Symbol tippen, dann „Zum Home-Bildschirm“. Die App startet danach im Vollbild und funktioniert offline.",
  dismiss: "Ausblenden",
  noMistakes: "Keine Fehler — gut gemacht!",

  // statistics ring
  fitForTest: "Fit für die Prüfung",
  statsCorrect: "Richtig",
  statsWrong: "Falsch",
  statsUnseen: "Offen",
  statsRingLabel: "{percent}% fit — {correct} richtig, {wrong} falsch, {unseen} offen von {total} Fragen",
  allTopics: "Alle Themen",
  topicProgress: "{correct} von {total} richtig",
  scMark: "Frage markieren",

  // navigation drawer
  menu: "Menü",
  openMenu: "Menü öffnen",
  closeMenu: "Menü schließen",
  allQuestionsTopics: "Alle Fragen & Themen",
  sampleSheets: "Musterbögen",
  help: "Hilfe",
  learningTips: "Lerntipps",
  support: "Support",
  imprint: "Impressum",

  // sample sheets
  sheetsTitle: "Musterbögen",
  sheetsHint: "Amtliche Prüfungsbögen mit je 30 Fragen. Wähle einen Bogen aus.",
  sheetLabel: "Bogen {n}",
  sheetRead: "Bogen durchlesen",
  sheetPractice: "Bogen üben",
  sheetExam: "Prüfungsmodus",
  sheetDone: "{correct} von {total} richtig",

  // account & sync
  account: "Konto",
  signIn: "Anmelden",
  signUp: "Konto erstellen",
  signOut: "Abmelden",
  username: "Benutzername",
  password: "Passwort",
  accountIntro: "Mit einem Konto wird dein Lernfortschritt zwischen deinen Geräten abgeglichen — zum Beispiel zwischen Mac und iPhone.",
  accountNoServer: "Es werden nur Benutzername, Passwort-Hash und dein Lernfortschritt gespeichert. Der Server läuft auf deinem eigenen Rechner.",
  accountLanHint: "Auf dem iPhone dieselbe Adresse im Browser öffnen, die beim Serverstart als Netzwerk-Adresse angezeigt wird, und dort mit demselben Konto anmelden.",
  signedInAs: "Angemeldet als {name}",
  syncNow: "Jetzt abgleichen",
  syncIdle: "Abgeglichen",
  syncSyncing: "Wird abgeglichen…",
  syncNever: "Noch nicht abgeglichen",
  syncAt: "Zuletzt abgeglichen um {time}",
  haveAccount: "Du hast schon ein Konto? Anmelden",
  needAccount: "Noch kein Konto? Konto erstellen",
  passwordRule: "Mindestens 8 Zeichen.",
  errInvalidCredentials: "Benutzername oder Passwort ist falsch.",
  errUsernameTaken: "Dieser Benutzername ist bereits vergeben.",
  errPasswordTooShort: "Das Passwort muss mindestens 8 Zeichen lang sein.",
  errTooManyAttempts: "Zu viele Fehlversuche. Bitte in einigen Minuten erneut versuchen.",
  errNetwork: "Der Server ist nicht erreichbar.",
  errGeneric: "Etwas ist schiefgelaufen.",
};

export type UiStrings = typeof de;

const en: UiStrings = {
  appTitle: "Driving Theory",
  appSubtitle: "Local practice app",

  home: "Home",
  exam: "Exam",
  practice: "Practice",
  results: "Results",
  back: "Back",
  next: "Next",
  previous: "Previous",
  finish: "Finish exam",
  restart: "Restart",
  close: "Close",

  startExam: "Simulate the exam",
  startExamHint: "30 questions from an official exam sheet, just like the real test.",
  startPractice: "Practise all questions",
  startPracticeHint: "Browse the whole question catalogue freely, with explanations.",
  catalogInfo: "Question catalogue valid from {from}",
  questionCount: "{n} questions",

  primaryLanguage: "Primary language",
  secondaryLanguage: "Secondary language",
  secondaryOff: "off",
  languageSettings: "Languages",
  translationMissing: "No translation available — showing the German text.",

  question: "Question",
  questionOf: "Question {i} of {n}",
  points: "{n} error points",
  selectAll: "More than one answer may be correct",
  enterNumber: "Enter a number",
  yourAnswer: "Your answer",
  checkAnswer: "Check answer",
  correct: "Correct",
  incorrect: "Incorrect",
  correctAnswer: "Correct answer",
  explanation: "Explanation",
  showExplanation: "Show explanation",
  hideExplanation: "Hide explanation",
  showTranslation: "Show translation",
  hideTranslation: "Hide translation",
  unanswered: "Not answered",
  answered: "Answered",
  playVideo: "Play video",
  videoQuestion: "Video question",
  videoHint: "Watch the video before answering.",
  startFilm: "Start film",
  watchFilmPrompt: "Please start the film in order to familiarise yourself with the situation.",
  toAnswers: "Answer",
  replayFilm: "Play film again",
  filmPlaysLeft: "You can watch the film {n} more time(s).",
  filmPlaysUsedUp: "The film cannot be watched again.",
  questionDetail: "Question detail",
  detailNumber: "Question number",
  detailTopic: "Topic",
  detailPoints: "Error points",
  detailPractised: "Practised",
  detailNeverPractised: "Not answered yet.",
  detailLastPractised: "Last practised on {date}",
  detailTimesWrong: "times wrong",
  detailTimesRight: "times right answered",
  share: "Share",
  ok: "OK",

  examSet: "Sheet {n}",
  examProgress: "{answered} of {total} answered",
  confirmFinish: "Finish the exam and see your result?",
  confirmFinishOpen: "{n} questions are still open. Evaluate anyway?",
  cancel: "Cancel",
  confirm: "Evaluate",

  passed: "Passed",
  failed: "Not passed",
  errorPoints: "Error points",
  errorPointsOf: "{points} of a maximum {max} error points",
  scoreCorrect: "{correct} of {total} correct",
  resultSummary: "Summary",
  reviewMistakes: "Review mistakes",
  reviewAll: "Review all questions",
  failReasonPoints: "More than {max} error points.",
  failReasonTwoFive: "Two questions worth 5 error points answered incorrectly.",
  passRule: "You pass with at most {max} error points and no two 5-point questions wrong.",
  byCategory: "By topic",
  timeTaken: "Time taken",
  newExam: "New exam",

  filterAll: "All",
  filterMistakes: "My mistakes",
  filterUnseen: "Not seen yet",
  filterVideo: "Video questions",
  jumpToQuestion: "Jump to question",
  first: "First question",
  last: "Last question",
  back10: "Back 10 questions",
  forward10: "Forward 10 questions",
  searchPlaceholder: "Search questions…",
  noResults: "No questions found.",
  resetProgress: "Reset progress",
  resetProgressConfirm: "Delete all saved progress?",

  shortcuts: "Shortcuts",
  version: "Version",
  sw_unsupported: "Offline not available",
  sw_off: "Preparing offline use…",
  sw_installing: "Setting up offline use…",
  sw_active: "Available offline",
  sw_waiting: "Update ready",
  sw_failed: "Offline setup failed",
  settings: "Settings",
  settingsHint: "Offline use, storage and version.",
  offlineSection: "Offline use",
  cachedMedia: "Media files kept",
  storageUsed: "Storage used",
  precacheHint: "Download everything up front — catalogue, images and films, around 400 MB. The app then works with no network at all.",
  precacheStart: "Make everything available offline",
  precacheProgress: "{done} of {total} downloaded…",
  precacheDone: "{n} files stored.",
  precacheFailed: "{n} failed",
  versionCurrent: "Up to date.",
  versionStale: "out of date",
  updateAvailable: "A new version is available.",
  reloadNow: "Reload",
  shortcutsTitle: "Keyboard shortcuts",
  scToggleAnswer: "Select answer {n}",
  scNext: "Next question",
  scPrev: "Previous question",
  scCheck: "Check answer / confirm",
  scExplain: "Show or hide the explanation",
  scTranslate: "Show or hide the translation",
  scVideo: "Play the video",
  scSwapLang: "Swap the two languages",
  scCycleSecondary: "Change the secondary language",
  scHelp: "Show this help",
  scHome: "Back to home",
  scFinish: "Evaluate the exam",

  skipToContent: "Skip to content",
  loading: "Loading…",
  loadError: "The question catalogue could not be loaded.",
  mediaUnavailable: "Media unavailable",

  pointsShort: "Points",
  remainingTasks: "{n} questions left",
  evaluate: "Evaluate",
  markQuestion: "Mark question",
  unmarkQuestion: "Remove mark",
  basicKnowledge: "Basic knowledge",

  training: "Training",
  newQuestions: "30 new questions",
  newQuestionsHint: "Questions you have not yet answered correctly twice in a row.",
  allQuestions: "All questions",
  specialTopics: "Special topics",
  specialTopicsHint: "Practise by subject, such as right of way or traffic signs.",
  searchQuestions: "Search for questions",
  lastMistakes: "{n} last mistakes",
  markedQuestions: "{n} marked questions",
  allFit: "Every question is already “fit for the test” (last two attempts correct). Carry on under “All questions & topics” if you want more practice.",
  noMarked: "You have not marked any questions yet.",
  installTitle: "Add to Home Screen",
  installHint: "Safari never offers this by itself: tap the Share icon, then “Add to Home Screen”. The app then launches full-screen and works offline.",
  dismiss: "Dismiss",
  noMistakes: "No mistakes — well done!",

  fitForTest: "Fit for the test",
  statsCorrect: "Correct",
  statsWrong: "Wrong",
  statsUnseen: "Open",
  statsRingLabel: "{percent}% fit — {correct} correct, {wrong} wrong, {unseen} open of {total} questions",
  allTopics: "All topics",
  topicProgress: "{correct} of {total} correct",
  scMark: "Mark the question",

  menu: "Menu",
  openMenu: "Open menu",
  closeMenu: "Close menu",
  allQuestionsTopics: "All questions & topics",
  sampleSheets: "Sample sheets",
  help: "Help",
  learningTips: "Learning tips",
  support: "Support",
  imprint: "Imprint",

  sheetsTitle: "Sample sheets",
  sheetsHint: "Official exam sheets of 30 questions each. Pick one to begin.",
  sheetLabel: "Sheet {n}",
  sheetRead: "Read through",
  sheetPractice: "Practise sheet",
  sheetExam: "Exam mode",
  sheetDone: "{correct} of {total} correct",

  account: "Account",
  signIn: "Sign in",
  signUp: "Create account",
  signOut: "Sign out",
  username: "Username",
  password: "Password",
  accountIntro: "An account keeps your progress in step across your devices — between your Mac and your iPhone, for instance.",
  accountNoServer: "Only your username, a password hash and your study progress are stored. The server runs on your own machine.",
  accountLanHint: "On your phone, open the network address shown when the server starts and sign in with the same account.",
  signedInAs: "Signed in as {name}",
  syncNow: "Sync now",
  syncIdle: "In sync",
  syncSyncing: "Syncing…",
  syncNever: "Not synced yet",
  syncAt: "Last synced at {time}",
  haveAccount: "Already have an account? Sign in",
  needAccount: "No account yet? Create one",
  passwordRule: "At least 8 characters.",
  errInvalidCredentials: "That username or password is not right.",
  errUsernameTaken: "That username is already taken.",
  errPasswordTooShort: "The password must be at least 8 characters.",
  errTooManyAttempts: "Too many failed attempts. Try again in a few minutes.",
  errNetwork: "The server cannot be reached.",
  errGeneric: "Something went wrong.",
};

const zh: UiStrings = {
  appTitle: "德国驾照理论",
  appSubtitle: "本地练习应用",

  home: "首页",
  exam: "考试",
  practice: "练习",
  results: "成绩",
  back: "返回",
  next: "下一题",
  previous: "上一题",
  finish: "结束考试",
  restart: "重新开始",
  close: "关闭",

  startExam: "模拟考试",
  startExamHint: "从官方试卷中抽取 30 道题，与真实考试完全一致。",
  startPractice: "练习全部题目",
  startPracticeHint: "自由浏览整个题库，并查看详细解析。",
  catalogInfo: "题库有效期自 {from} 起",
  questionCount: "{n} 道题",

  primaryLanguage: "主语言",
  secondaryLanguage: "第二语言",
  secondaryOff: "关闭",
  languageSettings: "语言设置",
  translationMissing: "暂无翻译，显示德语原文。",

  question: "题目",
  questionOf: "第 {i} 题，共 {n} 题",
  points: "{n} 个错误分",
  selectAll: "可能有多个正确答案",
  enterNumber: "请输入数字",
  yourAnswer: "你的答案",
  checkAnswer: "检查答案",
  correct: "正确",
  incorrect: "错误",
  correctAnswer: "正确答案",
  explanation: "解析",
  showExplanation: "显示解析",
  hideExplanation: "隐藏解析",
  showTranslation: "显示翻译",
  hideTranslation: "隐藏翻译",
  unanswered: "未作答",
  answered: "已作答",
  playVideo: "播放视频",
  videoQuestion: "视频题",
  videoHint: "请先观看视频，然后作答。",
  startFilm: "播放影片",
  watchFilmPrompt: "请先播放影片，以熟悉该情形。",
  toAnswers: "作答",
  replayFilm: "重新播放影片",
  filmPlaysLeft: "这段影片你还可以观看 {n} 次。",
  filmPlaysUsedUp: "这段影片不能再观看了。",
  questionDetail: "题目详情",
  detailNumber: "题号",
  detailTopic: "主题",
  detailPoints: "错误分",
  detailPractised: "练习情况",
  detailNeverPractised: "尚未作答。",
  detailLastPractised: "最近练习于 {date}",
  detailTimesWrong: "次答错",
  detailTimesRight: "次答对",
  share: "分享",
  ok: "确定",

  examSet: "第 {n} 套试卷",
  examProgress: "已作答 {answered} / {total}",
  confirmFinish: "结束考试并查看成绩？",
  confirmFinishOpen: "还有 {n} 道题未作答，仍要评分吗？",
  cancel: "取消",
  confirm: "评分",

  passed: "通过",
  failed: "未通过",
  errorPoints: "错误分",
  errorPointsOf: "{points} 分，上限 {max} 分",
  scoreCorrect: "答对 {correct} / {total}",
  resultSummary: "成绩总结",
  reviewMistakes: "查看错题",
  reviewAll: "查看全部题目",
  failReasonPoints: "错误分超过 {max} 分。",
  failReasonTwoFive: "有两道 5 分题答错。",
  passRule: "错误分不超过 {max} 分，且 5 分题答错不超过一道，即为通过。",
  byCategory: "按主题分类",
  timeTaken: "用时",
  newExam: "开始新考试",

  filterAll: "全部",
  filterMistakes: "我的错题",
  filterUnseen: "尚未做过",
  filterVideo: "视频题",
  jumpToQuestion: "跳转到题目",
  first: "第一题",
  last: "最后一题",
  back10: "后退 10 题",
  forward10: "前进 10 题",
  searchPlaceholder: "搜索题目…",
  noResults: "未找到题目。",
  resetProgress: "重置练习记录",
  resetProgressConfirm: "确定删除所有已保存的记录吗？",

  shortcuts: "快捷键",
  version: "版本",
  sw_unsupported: "无法离线使用",
  sw_off: "正在准备离线功能…",
  sw_installing: "正在设置离线功能…",
  sw_active: "可离线使用",
  sw_waiting: "更新就绪",
  sw_failed: "离线功能设置失败",
  settings: "设置",
  settingsHint: "离线使用、存储与版本。",
  offlineSection: "离线使用",
  cachedMedia: "已缓存的媒体文件",
  storageUsed: "已占用存储",
  precacheHint: "提前下载全部内容——题库、图片和影片，约 400 MB。完成后应用可完全离线使用。",
  precacheStart: "让全部内容可离线使用",
  precacheProgress: "已下载 {done} / {total}…",
  precacheDone: "已保存 {n} 个文件。",
  precacheFailed: "{n} 个失败",
  versionCurrent: "已是最新版本。",
  versionStale: "已过期",
  updateAvailable: "有新版本可用。",
  reloadNow: "重新加载",
  shortcutsTitle: "键盘快捷键",
  scToggleAnswer: "选择第 {n} 个答案",
  scNext: "下一题",
  scPrev: "上一题",
  scCheck: "检查答案 / 确认",
  scExplain: "显示或隐藏解析",
  scTranslate: "显示或隐藏翻译",
  scVideo: "播放视频",
  scSwapLang: "交换两种语言",
  scCycleSecondary: "切换第二语言",
  scHelp: "显示此帮助",
  scHome: "返回首页",
  scFinish: "考试评分",

  skipToContent: "跳到主要内容",
  loading: "加载中…",
  loadError: "无法加载题库。",
  mediaUnavailable: "媒体文件不可用",

  pointsShort: "分数",
  remainingTasks: "还剩 {n} 题",
  evaluate: "评分",
  markQuestion: "标记题目",
  unmarkQuestion: "取消标记",
  basicKnowledge: "基础知识",

  training: "训练",
  newQuestions: "30 道新题",
  newQuestionsHint: "尚未连续两次答对的题目。",
  allQuestions: "全部题目",
  specialTopics: "专项主题",
  specialTopicsHint: "按主题练习，例如优先通行权或交通标志。",
  searchQuestions: "搜索题目",
  lastMistakes: "{n} 道最近错题",
  markedQuestions: "{n} 道已标记题目",
  allFit: "所有题目都已「备考合格」（最近两次作答均正确）。如需继续练习，请前往「全部题目与主题」。",
  noMarked: "你还没有标记任何题目。",
  installTitle: "添加到主屏幕",
  installHint: "Safari 不会主动提示：点按「分享」图标，然后选择「添加到主屏幕」。之后应用会以全屏方式启动，并可离线使用。",
  dismiss: "隐藏",
  noMistakes: "没有错题，做得好！",

  fitForTest: "考试准备度",
  statsCorrect: "答对",
  statsWrong: "答错",
  statsUnseen: "未做",
  statsRingLabel: "准备度 {percent}% — 共 {total} 题，答对 {correct}，答错 {wrong}，未做 {unseen}",
  allTopics: "全部主题",
  topicProgress: "答对 {correct} / {total}",
  scMark: "标记题目",

  menu: "菜单",
  openMenu: "打开菜单",
  closeMenu: "关闭菜单",
  allQuestionsTopics: "全部题目与主题",
  sampleSheets: "样卷",
  help: "帮助",
  learningTips: "学习建议",
  support: "支持",
  imprint: "版本说明",

  sheetsTitle: "样卷",
  sheetsHint: "官方试卷，每套 30 道题。请选择一套开始。",
  sheetLabel: "第 {n} 套",
  sheetRead: "通读试卷",
  sheetPractice: "练习本卷",
  sheetExam: "考试模式",
  sheetDone: "答对 {correct} / {total}",

  account: "账户",
  signIn: "登录",
  signUp: "创建账户",
  signOut: "退出登录",
  username: "用户名",
  password: "密码",
  accountIntro: "使用账户可在你的多台设备之间同步学习进度，例如 Mac 与 iPhone。",
  accountNoServer: "仅保存用户名、密码哈希和学习进度。服务器运行在你自己的电脑上。",
  accountLanHint: "在手机浏览器中打开服务器启动时显示的网络地址，并使用同一账户登录。",
  signedInAs: "已登录：{name}",
  syncNow: "立即同步",
  syncIdle: "已同步",
  syncSyncing: "正在同步…",
  syncNever: "尚未同步",
  syncAt: "上次同步：{time}",
  haveAccount: "已有账户？点此登录",
  needAccount: "还没有账户？点此创建",
  passwordRule: "至少 8 个字符。",
  errInvalidCredentials: "用户名或密码不正确。",
  errUsernameTaken: "该用户名已被使用。",
  errPasswordTooShort: "密码至少需要 8 个字符。",
  errTooManyAttempts: "失败次数过多，请几分钟后再试。",
  errNetwork: "无法连接到服务器。",
  errGeneric: "出现了一些问题。",
};

export const UI: Record<Lang, UiStrings> = { de, en, zh };

/** Looks up a UI string and substitutes `{placeholder}` values. */
export function t(
  lang: Lang,
  key: keyof UiStrings,
  vars?: Record<string, string | number>
): string {
  const s = UI[lang][key] ?? UI.de[key];
  if (!vars) return s;
  return s.replace(/\{(\w+)\}/g, (m, k: string) =>
    k in vars ? String(vars[k]) : m
  );
}
